import { createContext, useContext, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import { type SosAlertRow, type SosAlertView, toSosAlertView } from "./sos.model";

const SOS_KEY = ["therapist-sos"] as const;
const OPEN_SOS_KEY = [...SOS_KEY, "open"] as const;

/**
 * Live SOS alerts — everything not yet RESOLVED (0277). Acknowledging no longer
 * takes an SOS off screen; it moves it to "being handled" until someone closes
 * it with a note, so a mis-clicked Acknowledge hides nothing.
 *
 * A plain RLS read: a Hub Manager sees their own hub's, admin and superadmin see
 * all, and the policy is the only scope (no client-side hub filter to drift).
 * Polled every 10 s: an SOS must not wait for a page refresh.
 */
export function useOpenSosAlerts() {
  return useQuery({
    queryKey: OPEN_SOS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_sos_alerts")
        .select(
          "id, ref, raised_at, danger_type, booking_ids, location_captured_at, geo, hub_id, acknowledged_at, hub:hubs(name), therapist:staff!therapist_sos_alerts_therapist_id_fkey(full_name), handler:staff!therapist_sos_alerts_acknowledged_by_fkey(full_name)",
        )
        .filter("resolved_at", "is", null)
        .order("raised_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      const rows = (data ?? []) as unknown as SosAlertRow[];

      // Refs only — the booking's client never enters this query.
      const bookingIds = [...new Set(rows.flatMap((row) => row.booking_ids))];
      const refsById = new Map<string, string>();
      if (bookingIds.length > 0) {
        const { data: bookings, error: refsError } = await supabase
          .from("bookings")
          .select("id, ref")
          .in("id", bookingIds);
        if (refsError) throw refsError;
        for (const booking of bookings ?? []) refsById.set(booking.id, booking.ref);
      }
      // Open ones first, then those being handled; newest first within each.
      return rows
        .map((row) => toSosAlertView(row, refsById))
        .sort((a, b) => (a.stage === b.stage ? 0 : a.stage === "open" ? -1 : 1));
    },
    refetchInterval: 10_000,
  });
}

/**
 * Move the map to an SOS once — the first time it is seen with a location,
 * never again on a refetch. Unlike an ordinary alert, an SOS already open when
 * the page loads IS news: nobody has acknowledged it yet.
 *
 * Waits for `ready` (the map exists), so an SOS that arrives before the map has
 * loaded is not marked as shown without having been shown.
 */
export function useFocusNewSos(
  alerts: readonly SosAlertView[] | undefined,
  ready: boolean,
  focus: (alert: SosAlertView) => void,
) {
  const focused = useRef(new Set<string>());
  const focusRef = useRef(focus);
  focusRef.current = focus;

  useEffect(() => {
    if (!ready || !alerts) return;
    const fresh = alerts.filter((alert) => alert.point && !focused.current.has(alert.id));
    for (const alert of fresh) focused.current.add(alert.id);
    // Newest first (the query's order): one camera move, to the latest.
    const [latest] = fresh;
    if (latest) focusRef.current(latest);
  }, [alerts, ready]);
}

// ─── actions ────────────────────────────────────────────────────────────────

/**
 * What the SOS cards can do. One shape for the real API and for the live map's
 * simulator (`?demo=1`), so the same cards drive both and the simulator never
 * touches a real alert.
 */
export interface SosActions {
  acknowledge: (alertId: string) => Promise<unknown>;
  resolve: (alertId: string, note: string) => Promise<unknown>;
  /** The alert an action is running for, so only its buttons wait. */
  pendingId: string | null;
}

/** Set by the live map in simulator mode; everywhere else it is absent. */
export const SosActionsContext = createContext<SosActions | null>(null);

/** Acknowledge, then resolve — the real API. Hub scope is enforced in SQL. */
function useRealSosActions(): SosActions {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: SOS_KEY });
  const acknowledge = useMutation({
    mutationFn: (alertId: string) =>
      callEdgeFunction("admin/therapist-sos/acknowledge", { data: { alertId } }),
    onSuccess: refresh,
  });
  const resolve = useMutation({
    mutationFn: (input: { alertId: string; note: string }) =>
      callEdgeFunction("admin/therapist-sos/resolve", { data: input }),
    onSuccess: refresh,
  });
  return {
    acknowledge: (alertId) => acknowledge.mutateAsync(alertId),
    resolve: (alertId, note) => resolve.mutateAsync({ alertId, note }),
    pendingId: acknowledge.isPending
      ? (acknowledge.variables ?? null)
      : resolve.isPending
        ? (resolve.variables?.alertId ?? null)
        : null,
  };
}

/** The simulator's actions when inside one, otherwise the real API's. */
export function useSosActions(): SosActions {
  const real = useRealSosActions();
  return useContext(SosActionsContext) ?? real;
}
